import React, { useState, useEffect, useCallback } from 'react';
import {
  getKingdomVillages,
  UzairueVillage,
  KINGDOM_DATA_CHANGE_EVENT,
} from '../../utils/kingdomDataStorage';

const CATS = ['All', 'Royal Regalia', 'Historical Artifacts', 'Masquerade & Music', 'Festival Moments'];

const S = {
  pageBg:      { background: '#070D0A' },
  surface:     { background: '#0E1812' },
  card:        { background: '#121E16' },
  border:      '1px solid #1C2E22',
  borderGold:  '1px solid rgba(201,168,76,0.3)',
  textPrimary: { color: '#EDE8DC' },
  textMuted:   { color: 'rgba(237,232,220,0.5)' },
  gold:        { color: '#C9A84C' },
  cinzel:      { fontFamily: 'Cinzel, serif' },
};

export const CulturalHeritage: React.FC = () => {
  const [selectedCommunity, setSelectedCommunity] = useState<string | null>(null);
  const [villages, setVillages] = useState<UzairueVillage[]>(() => getKingdomVillages());
  const [items, setItems]       = useState<any[]>([]);
  const [loading, setLoading]   = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [activeItemIndex, setActiveItemIndex]   = useState<number | null>(null);
  const [hoveredItemId, setHoveredItemId]       = useState<string | null>(null);
  const [imageErrorMap, setImageErrorMap]       = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handleUpdate = (e: any) => {
      if (e.detail?.villages) setVillages(e.detail.villages);
      else setVillages(getKingdomVillages());
    };
    window.addEventListener(KINGDOM_DATA_CHANGE_EVENT, handleUpdate);
    return () => window.removeEventListener(KINGDOM_DATA_CHANGE_EVENT, handleUpdate);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/public/heritage');
      const d = await r.json();
      if (d.success) setItems(d.items || []);
    } catch(e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredItems = items.filter(
    item => selectedCategory === 'All' || item.category === selectedCategory
  );
  const activeItem = activeItemIndex !== null ? filteredItems[activeItemIndex] : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (activeItemIndex === null) return;
      if (e.key === 'Escape') setActiveItemIndex(null);
      if (e.key === 'ArrowRight') setActiveItemIndex(prev => prev !== null ? (prev + 1) % filteredItems.length : null);
      if (e.key === 'ArrowLeft')  setActiveItemIndex(prev => prev !== null ? (prev - 1 + filteredItems.length) % filteredItems.length : null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeItemIndex, filteredItems.length]);

  return (
    <div style={{ paddingBottom: 64 }} className="space-y-12">

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #3D1E16, #2A140E, #3D1E16)',
        border: '2px solid rgba(212,175,55,0.5)', borderRadius: 24, padding: '48px 32px',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }} className="space-y-4">
        <div style={{
          display: 'inline-block', padding: '4px 12px',
          background: 'rgba(212,175,55,0.15)', color: '#D4AF37',
          fontSize: 11, fontWeight: 900, textTransform: 'uppercase',
          letterSpacing: 3, borderRadius: 999, border: '1px solid rgba(212,175,55,0.3)',
        }}>🏛️ Kingdom History & Heritage Mandate</div>
        <h1 style={{ ...S.cinzel, fontSize: 'clamp(24px,5vw,44px)', fontWeight: 900, color: '#fff', margin: '8px 0' }}>
          The Noble Legacy & <span style={{ color: '#D4AF37' }}>5 Pillars of Uzairue</span>
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,235,180,0.85)', maxWidth: 700, margin: '0 auto', lineHeight: 1.7 }}>
          Situated in Etsako West Local Government Area of Edo State, Nigeria, Uzairue is an ancient kingdom
          grounded in rich agrarian soil, natural mineral wealth, royal leadership, and peaceful unity.
        </p>
      </div>

      {/* 5 Pillars */}
      <div style={{
        background: 'linear-gradient(135deg, #3D1E16, #2A140E)',
        border: '2px solid rgba(212,175,55,0.5)', borderRadius: 24, padding: '40px 32px',
      }} className="space-y-8">
        <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }} className="space-y-2">
          <span style={{
            fontSize: 10, fontWeight: 900, textTransform: 'uppercase', color: '#D4AF37', letterSpacing: 3,
            background: '#3D1E16', padding: '3px 12px', borderRadius: 999,
            border: '1px solid rgba(212,175,55,0.3)', display: 'inline-block',
          }}>Royal Emblem Foundation</span>
          <h2 style={{ ...S.cinzel, fontSize: 26, fontWeight: 900, color: '#D4AF37', margin: '6px 0' }}>
            The 5 Pillars of Uzairue Kingdom
          </h2>
          <p style={{ fontSize: 11, color: 'rgba(255,235,180,0.85)' }}>
            The foundational core values depicted on the official Royal Seal guiding our culture and development.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { icon: '🌾', label: 'Pillar #1', title: 'Our Soil',       desc: 'Agrarian lifestyle, community farming, and rich harvest celebrations.',                          accent: '#2E5A1C' },
            { icon: '👑', label: 'Pillar #2', title: 'Our Heritage',   desc: 'Royal crown, sacred palm traditions, masquerades, and cultural pride.',                        accent: '#D4AF37' },
            { icon: '🪨', label: 'Pillar #3', title: 'Our Wealth',     desc: 'Kaolin Clay, Granite, Laterite deposits & economic mineral processing.',                       accent: '#9E472A' },
            { icon: '🐘', label: 'Pillar #4', title: 'Our People',     desc: 'Peace, strength, resilience, and security across all 22 villages.',                            accent: '#E67E22' },
            { icon: '🏛️', label: 'Pillar #5', title: 'Our Leadership', desc: 'The Ogieneni in Council, traditional governance, and wisdom.',                                 accent: '#D4AF37' },
          ].map(p => (
            <div key={p.title} style={{
              background: '#2A140E', borderRadius: 16, padding: 20,
              border: `1px solid ${p.accent}`, textAlign: 'center',
            }} className="space-y-2">
              <span style={{ fontSize: 28, display: 'block' }}>{p.icon}</span>
              <span style={{
                fontSize: 10, fontWeight: 900, textTransform: 'uppercase',
                background: p.accent, color: p.accent === '#D4AF37' ? '#1a0e00' : '#fff',
                padding: '2px 8px', borderRadius: 4, display: 'block',
              }}>{p.label}</span>
              <h3 style={{ ...S.cinzel, fontSize: 13, fontWeight: 900, color: '#D4AF37', margin: '4px 0' }}>{p.title}</h3>
              <p style={{ fontSize: 11, color: 'rgba(255,235,180,0.75)', lineHeight: 1.5 }}>{p.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Gallery */}
      <div style={{ ...S.surface, border: S.borderGold, borderRadius: 24, padding: '40px 32px' }} className="space-y-8">
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, paddingBottom: 16, borderBottom: S.border }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 22 }}>📸</span>
              <h2 style={{ ...S.cinzel, ...S.gold, fontSize: 20, fontWeight: 900, margin: 0 }}>
                Kingdom Historical Artifacts & Festival Gallery
              </h2>
            </div>
            <p style={{ ...S.textMuted, fontSize: 12, marginTop: 4 }}>
              Click any photograph to launch the interactive high-resolution lightbox viewer with historical provenance notes.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {CATS.map(cat => (
              <button key={cat} onClick={() => { setSelectedCategory(cat); setActiveItemIndex(null); }}
                style={{
                  padding: '6px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: selectedCategory === cat ? '#0F5132' : 'rgba(201,168,76,0.08)',
                  color: selectedCategory === cat ? '#C9A84C' : 'rgba(237,232,220,0.6)',
                  border: selectedCategory === cat ? '1px solid #C9A84C' : '1px solid rgba(201,168,76,0.2)',
                }}>{cat}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(237,232,220,0.35)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
            <p style={{ ...S.cinzel, fontSize: 14, margin: 0 }}>Loading heritage gallery...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(237,232,220,0.35)' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🏛️</div>
            <p style={{ ...S.cinzel, fontSize: 16, margin: '0 0 8px', color: 'rgba(237,232,220,0.6)' }}>No heritage items yet</p>
            <p style={{ fontSize: 12, margin: 0 }}>
              {selectedCategory !== 'All' ? `No items in "${selectedCategory}". Try a different category.` : 'Heritage gallery items will appear here once added by administrators.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredItems.map((item: any, idx: number) => {
              const hasError = imageErrorMap[item.id];
              return (
                <div key={item.id}
                  onMouseEnter={() => setHoveredItemId(item.id)}
                  onMouseLeave={() => setHoveredItemId(null)}
                  onClick={() => setActiveItemIndex(idx)}
                  style={{
                    background: '#121E16', borderRadius: 16, overflow: 'hidden',
                    border: '2px solid rgba(201,168,76,0.25)', cursor: 'pointer',
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                    transition: 'border-color 0.2s, box-shadow 0.2s', position: 'relative',
                  }}
                  onMouseOver={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.7)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 32px rgba(0,0,0,0.5)'; }}
                  onMouseOut={e =>  { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.25)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                >
                  <div style={{ position: 'relative', height: 192, background: '#070D0A', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {item.imageUrl && !hasError ? (
                      <img src={item.imageUrl} alt={item.title}
                        onError={() => setImageErrorMap(prev => ({ ...prev, [item.id]: true }))}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        style={{ opacity: 0.9 }}/>
                    ) : (
                      <div style={{ textAlign: 'center', padding: 16 }}>
                        <span style={{ fontSize: 36, display: 'block', marginBottom: 8 }}>{item.fallbackEmoji || '🏛️'}</span>
                        <span style={{ fontSize: 10, fontFamily: 'monospace', color: '#C9A84C' }}>{item.title}</span>
                      </div>
                    )}
                    <span style={{
                      position: 'absolute', top: 10, left: 10, background: 'rgba(7,13,10,0.85)',
                      backdropFilter: 'blur(6px)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.4)',
                      fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 4,
                      textTransform: 'uppercase', letterSpacing: 1,
                    }}>{item.category}</span>
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      style={{ background: 'rgba(7,13,10,0.65)' }}>
                      <span style={{
                        background: '#C9A84C', color: '#070D0A', padding: '6px 14px', borderRadius: 10,
                        fontWeight: 900, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1,
                        display: 'flex', alignItems: 'center', gap: 4,
                      }}>🔍 Open Lightbox</span>
                    </div>
                  </div>
                  <div style={{ padding: 14, background: '#121E16', borderTop: S.border, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, fontFamily: 'monospace', color: 'rgba(237,232,220,0.4)', marginBottom: 4 }}>
                        <span>{item.yearEst}</span>
                        {item.communityProvenance && <span>📍 {item.communityProvenance}</span>}
                      </div>
                      <h3 style={{ ...S.cinzel, ...S.gold, fontSize: 12, fontWeight: 900, margin: '0 0 6px', lineHeight: 1.4 }}>{item.title}</h3>
                    </div>
                    {item.shortTooltip && (
                      <p style={{ fontSize: 11, color: 'rgba(237,232,220,0.6)', lineHeight: 1.5, margin: 0,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {item.shortTooltip}
                      </p>
                    )}
                  </div>
                  {hoveredItemId === item.id && item.shortTooltip && (
                    <div style={{
                      position: 'absolute', bottom: 60, left: 10, right: 10, zIndex: 20,
                      background: 'rgba(7,13,10,0.97)', color: '#E2C97E',
                      fontSize: 11, padding: 10, borderRadius: 10,
                      border: '1px solid rgba(201,168,76,0.5)', backdropFilter: 'blur(8px)', pointerEvents: 'none',
                    }}>
                      <div style={{ fontWeight: 800, color: '#C9A84C', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>💡 Historical Provenance:</div>
                      <p style={{ margin: 0, lineHeight: 1.5 }}>{item.shortTooltip}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {activeItem && activeItemIndex !== null && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(7,13,10,0.95)',
          backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
          <div style={{
            background: '#0E1812', border: '2px solid rgba(201,168,76,0.6)', borderRadius: 24,
            maxWidth: 900, width: '100%', maxHeight: '92vh', overflowY: 'auto',
            position: 'relative', display: 'flex', flexDirection: 'column', color: '#EDE8DC',
          }}>
            <button onClick={() => setActiveItemIndex(null)} style={{
              position: 'absolute', top: 14, right: 14, zIndex: 30, background: '#C9A84C', color: '#070D0A',
              width: 36, height: 36, borderRadius: '50%', fontWeight: 900, fontSize: 16, border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}>✕</button>

            <div style={{ flex: '0 0 60%', background: '#070D0A', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, minHeight: 320 }}>
              {activeItem.imageUrl && !imageErrorMap[activeItem.id] ? (
                <img src={activeItem.imageUrl} alt={activeItem.title}
                  style={{ maxHeight: '72vh', width: 'auto', objectFit: 'contain', borderRadius: 12 }}/>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: 64, display: 'block', marginBottom: 12 }}>{activeItem.fallbackEmoji || '🏛️'}</span>
                  <p style={{ ...S.cinzel, ...S.gold, fontSize: 16, fontWeight: 900 }}>{activeItem.title}</p>
                </div>
              )}
              <button onClick={() => setActiveItemIndex(prev => prev !== null ? (prev - 1 + filteredItems.length) % filteredItems.length : null)}
                style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(14,24,18,0.85)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.4)', width: 40, height: 40, borderRadius: '50%', fontSize: 22, fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
              <button onClick={() => setActiveItemIndex(prev => prev !== null ? (prev + 1) % filteredItems.length : null)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'rgba(14,24,18,0.85)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.4)', width: 40, height: 40, borderRadius: '50%', fontSize: 22, fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
              <span style={{ position: 'absolute', bottom: 10, left: 14, background: 'rgba(7,13,10,0.85)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.3)', fontSize: 10, fontFamily: 'monospace', fontWeight: 700, padding: '3px 10px', borderRadius: 999 }}>{activeItemIndex + 1} of {filteredItems.length}</span>
            </div>

            <div style={{ flex: '0 0 40%', padding: '32px 28px', background: '#0E1812', borderTop: S.border, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 16 }}>
              <div className="space-y-4">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ background: 'rgba(201,168,76,0.12)', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.35)', fontSize: 9, fontWeight: 800, padding: '3px 10px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 1 }}>{activeItem.category}</span>
                  {activeItem.yearEst && <span style={{ fontSize: 11, fontFamily: 'monospace', color: '#6EE7A0', fontWeight: 700 }}>Est: {activeItem.yearEst}</span>}
                </div>
                <h2 style={{ ...S.cinzel, color: '#C9A84C', fontSize: 20, fontWeight: 900, margin: 0, lineHeight: 1.3 }}>{activeItem.title}</h2>
                {activeItem.communityProvenance && (
                  <div style={{ background: '#070D0A', borderRadius: 10, border: S.border, padding: 12 }}>
                    <div style={{ fontSize: 10, fontWeight: 800, color: '#C9A84C', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>📍 Provenance & Custodianship:</div>
                    <p style={{ margin: 0, fontSize: 12, color: 'rgba(201,168,76,0.8)' }}>{activeItem.communityProvenance}</p>
                  </div>
                )}
                <div>
                  <h4 style={{ fontSize: 11, fontWeight: 700, color: 'rgba(237,232,220,0.55)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Historical Record & Cultural Significance</h4>
                  <p style={{ fontSize: 12, color: 'rgba(237,232,220,0.75)', lineHeight: 1.7, margin: 0 }}>{activeItem.fullDescription}</p>
                </div>
              </div>
              <div style={{ paddingTop: 16, borderTop: S.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, fontFamily: 'monospace', color: 'rgba(237,232,220,0.3)' }}>Uzairue Historical Society Archive</span>
                <button onClick={() => setActiveItemIndex(null)} style={{ background: '#C9A84C', color: '#070D0A', padding: '8px 16px', borderRadius: 10, border: 'none', fontWeight: 900, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div style={{ ...S.card, border: S.borderGold, borderRadius: 16, padding: 32, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }} className="space-y-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(201,168,76,0.15)', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>👑</div>
            <div>
              <h2 style={{ ...S.cinzel, ...S.gold, fontSize: 18, fontWeight: 900, margin: '0 0 2px' }}>The Ogieneni Stool & Kingdom Foundation</h2>
              <p style={{ fontSize: 10, color: '#C9A84C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: 0 }}>Paramount Rulership of Uzairue</p>
            </div>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(237,232,220,0.8)', lineHeight: 1.75, margin: 0 }}>
            The paramount ruler of Uzairue Kingdom bears the exalted royal title <strong style={{ color: '#C9A84C' }}>"The Ogieneni of Uzairue"</strong>.
            The Ogieneni throne is the spiritual, cultural, and administrative nucleus for all Uzairue indigenes worldwide.
          </p>
          <p style={{ fontSize: 13, color: 'rgba(237,232,220,0.8)', lineHeight: 1.75, margin: 0 }}>
            Historically originating from ancient Edo lineage migrations, the founding fathers settled in the lush,
            fertile valleys of northern Edo State, establishing towns that grew into the vibrant 22 villages and communities of Uzairue today.
          </p>
          <div style={{ background: 'rgba(15,81,50,0.15)', border: '1px solid rgba(15,81,50,0.4)', borderRadius: 10, padding: 14, fontSize: 12, color: '#6EE7A0', lineHeight: 1.7 }}>
            <strong>💡 Cultural Significance:</strong> "Uzairue" signifies unity, resilience, and peaceful governance.
            The Ogieneni rules in council with the High Chiefs and Village Heads (Odafe).
          </div>
        </div>
        <div style={{ ...S.card, border: S.borderGold, borderRadius: 16, padding: 32, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }} className="space-y-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: 'rgba(15,81,50,0.25)', fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🏮</div>
            <div>
              <h2 style={{ ...S.cinzel, ...S.gold, fontSize: 18, fontWeight: 900, margin: '0 0 2px' }}>The Essence of ERUE Festival</h2>
              <p style={{ fontSize: 10, color: '#6EE7A0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, margin: 0 }}>Harvest, Initiation & Reconciliation</p>
            </div>
          </div>
          <p style={{ fontSize: 13, color: 'rgba(237,232,220,0.8)', lineHeight: 1.75, margin: 0 }}>
            <strong style={{ color: '#C9A84C' }}>ERUE Festival</strong> is celebrated annually as the culmination of the harvest season, thanksgiving,
            and civic initiation. It marks the graduation of young men and women into age-grade societies responsible for community development.
          </p>
          <p style={{ fontSize: 13, color: 'rgba(237,232,220,0.8)', lineHeight: 1.75, margin: 0 }}>
            During ERUE, all inter-family disputes are peacefully resolved under the royal amnesty, ancestral masquerades perform sacred blessings,
            and traditional wrestling displays test the physical strength and discipline of young warriors.
          </p>
          <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 10, padding: 14, fontSize: 12, color: '#E2C97E', lineHeight: 1.7 }}>
            <strong>🌾 Age-Grade System:</strong> Youths advance through structured age-grades (e.g. Otu), performing civic duties
            such as roads maintenance, security, and festival organization.
          </div>
        </div>
      </div>

      {/* 22 Villages */}
      <div style={{ ...S.surface, border: '1px solid rgba(201,168,76,0.2)', borderRadius: 24, padding: 32 }} className="space-y-6">
        <div style={{ textAlign: 'center', maxWidth: 640, margin: '0 auto' }} className="space-y-2">
          <span style={{ fontSize: 10, fontWeight: 900, textTransform: 'uppercase', color: '#C9A84C', letterSpacing: 3, background: 'rgba(201,168,76,0.08)', padding: '3px 12px', borderRadius: 999, border: '1px solid rgba(201,168,76,0.25)', display: 'inline-block' }}>
            Kingdom Geography ({villages.length} Communities)
          </span>
          <h2 style={{ ...S.cinzel, ...S.textPrimary, fontSize: 'clamp(20px,3vw,28px)', fontWeight: 900, margin: '6px 0' }}>
            The 22 Villages & Component Communities of Uzairue
          </h2>
          <p style={{ ...S.textMuted, fontSize: 12, margin: 0 }}>
            Each of the 22 villages brings unique craftsmanship, agricultural mastery, and cultural folklore to the ERUE Festival.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {villages.map((v, idx) => {
            const isSelected = selectedCommunity === v.name;
            return (
              <div key={v.id} onClick={() => setSelectedCommunity(isSelected ? null : v.name)}
                style={{ background: isSelected ? '#0F5132' : '#070D0A', border: isSelected ? '1px solid #C9A84C' : S.border, borderRadius: 16, padding: 20, cursor: 'pointer', transition: 'all 0.2s', boxShadow: isSelected ? '0 0 0 2px rgba(201,168,76,0.25)' : 'none' }}
                className="space-y-3">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 22 }}>🏛️</span>
                  <span style={{ fontSize: 9, fontWeight: 800, textTransform: 'uppercase', background: 'rgba(15,81,50,0.5)', color: '#C9A84C', border: '1px solid #1C4A2A', padding: '2px 8px', borderRadius: 4 }}>Community #{idx + 1}</span>
                </div>
                <div>
                  <h3 style={{ ...S.cinzel, color: '#C9A84C', fontSize: 14, fontWeight: 900, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</h3>
                  <p style={{ fontSize: 12, color: 'rgba(237,232,220,0.7)', fontWeight: 500, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>👑 {v.dauduOrLeader}</p>
                </div>
                <div style={{ paddingTop: 8, borderTop: S.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: 'rgba(237,232,220,0.4)' }}>
                  <span>Pop: {v.populationEstimate}</span>
                  <span style={{ color: '#C9A84C', fontWeight: 700 }}>{isSelected ? 'Active' : 'Details →'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cultural Traditions */}
      <div className="space-y-6">
        <h2 style={{ ...S.cinzel, ...S.gold, fontSize: 22, fontWeight: 900, textAlign: 'center', margin: 0 }}>
          Key Pillars of Uzairue Cultural Tradition
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: '🎭', title: 'Ancestral Masquerades (Igue)', desc: 'Sacred masquerades clad in woven raffia, cowries, and intricate wood carvings represent ancestral spirits bestowing fertility, rain, and health upon the kingdom.' },
            { icon: '🤼', title: 'Traditional Afemai Wrestling',  desc: 'Unarmed wrestling competitions held in village square arenas. Contestants are cheered on by drumming ensembles, demonstrating honor, sportsmanship, and strength.' },
            { icon: '💃', title: 'Royal Maiden & Warrior Dances', desc: 'Exquisite dance troupes adorned with coral beads, brass anklets, and woven cloths perform rhythmic choreography celebrating womanhood, bravery, and royal dignity.' },
          ].map(t => (
            <div key={t.title} style={{ ...S.card, border: S.border, borderRadius: 16, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }} className="space-y-3">
              <div style={{ fontSize: 28 }}>{t.icon}</div>
              <h3 style={{ ...S.cinzel, ...S.gold, fontSize: 14, fontWeight: 900, margin: 0 }}>{t.title}</h3>
              <p style={{ fontSize: 12, color: 'rgba(237,232,220,0.65)', lineHeight: 1.7, margin: 0 }}>{t.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};
